import React, { useMemo } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Progress, Note, Kpi } from "./ui";
import { Icon } from "./Icons";
import { calcNacional, calcPorRegional, statusMetaColetiva } from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// GLOBAL — meta mensal da operação inteira (Caminho A)
// ═══════════════════════════════════════════════════════════════════════

export default function Nacional({ corretores, metas }) {
  const g = useMemo(() => calcNacional(corretores, "mes"), [corretores]);
  const regs = useMemo(() => calcPorRegional(corretores), [corretores]);
  const m = metas.nacional;

  const st = statusMetaColetiva({
    atingidoVol: g.valor, atingidoAtiv: g.ativados,
    metaVol: m.metaVolume, metaAtiv: m.metaAtivacoes,
  });

  const statusInfo = {
    supermeta:  { label: "Supermeta 🏆",   color: T.ok,    bg: T.okSoft,    icon: "🏆" },
    volume:     { label: "Meta volume",    color: T.brand, bg: T.brandSoft, icon: "💰" },
    ativacoes:  { label: "Meta ativações", color: T.info,  bg: T.infoSoft,  icon: "👥" },
    parcial:    { label: "Em progresso",   color: T.textSub, bg: T.cardSoft, icon: "○" },
  }[st];

  const pctVol = Math.min((g.valor / m.metaVolume) * 100, 100);
  const pctAtiv = Math.min((g.ativados / m.metaAtivacoes) * 100, 100);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Nacional"
        subtitle={`Operação inteira Bellanor · ${fmt.brl(m.metaVolume)} em volume E ${m.metaAtivacoes} ativações por mês`}
        icon={<Icon.globe size={24} />}
      />

      <div style={{ display: "grid", gap: 10 }}>
        {m.premioVolume && <Note tone="brand" icon={<Icon.gift size={14} />}><b>Meta Volume:</b> {m.premioVolume}</Note>}
        {m.premioAtivacoes && <Note tone="info" icon={<Icon.users size={14} />}><b>Meta Ativações:</b> {m.premioAtivacoes}</Note>}
        {m.premioSupermeta && <Note tone="ok" icon={<Icon.rocket size={14} />}><b>🏆 Supermeta:</b> {m.premioSupermeta}</Note>}
      </div>

      {/* HERO */}
      <div style={{
        background: `linear-gradient(135deg, ${statusInfo.bg} 0%, ${T.card} 100%)`,
        border: `1px solid ${statusInfo.color}44`,
        borderRadius: T.r.xl, padding: 28,
        borderLeft: `4px solid ${statusInfo.color}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: T.textSub, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>
              Volume total do mês
            </div>
            <div className="serif" style={{
              fontSize: 48, fontWeight: 600, color: T.brand,
              letterSpacing: -1.5, lineHeight: 1, marginTop: 6,
            }}>
              {fmt.brl(g.valor)}
            </div>
            <div style={{ fontSize: 12, color: T.textSub, marginTop: 6 }}>
              <b style={{ color: T.text }}>{g.ativados}</b> ativações vivas · <b style={{ color: T.text }}>{g.estadosAtivos}</b> {g.estadosAtivos === 1 ? "estado ativo" : "estados ativos"}
            </div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: statusInfo.color,
              padding: "6px 14px", borderRadius: 100,
              background: "rgba(255,255,255,0.6)",
              border: `1px solid ${statusInfo.color}44`,
              letterSpacing: 0.5, textTransform: "uppercase",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              {statusInfo.icon} {statusInfo.label}
            </div>
          </div>
        </div>

        {/* 2 metas */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
              💰 Volume até a meta
            </div>
            <Progress value={g.valor} max={m.metaVolume} color={pctVol >= 100 ? T.ok : T.brand} height={10} />
            <div style={{ fontSize: 10, color: T.textSub, marginTop: 6, display: "flex", justifyContent: "space-between" }}>
              <span>{fmt.brl(g.valor)}</span>
              <span>{pctVol.toFixed(0)}% de {fmt.brl(m.metaVolume)}</span>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
              👥 Ativações até a meta
            </div>
            <Progress value={g.ativados} max={m.metaAtivacoes} color={pctAtiv >= 100 ? T.ok : T.info} height={10} />
            <div style={{ fontSize: 10, color: T.textSub, marginTop: 6, display: "flex", justifyContent: "space-between" }}>
              <span>{g.ativados} ativações</span>
              <span>{pctAtiv.toFixed(0)}% de {m.metaAtivacoes}</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
        <Kpi label="Vendas diretas" value={g.vendas} sub="pagas no ciclo" accent={T.brand} icon={<Icon.handshake size={16} />} />
        <Kpi label="Pontos coletivos" value={g.pts} sub="base de ranking" accent={T.info} icon={<Icon.star size={16} />} />
        <Kpi label="Cadastros totais" value={g.cadastrados} sub={`${g.cadastrados > 0 ? ((g.ativados/g.cadastrados)*100).toFixed(0) : 0}% ativação`} accent={T.ok} icon={<Icon.users size={16} />} />
        <Kpi label="Estados ativos" value={g.estadosAtivos} sub="regionais" accent={T.warn} icon={<Icon.globe size={16} />} />
      </div>

      {/* Contribuição por regional */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 14 }}>
          Contribuição por Regional
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {regs.sort((a, b) => b.valor - a.valor).map(r => {
            const pct = g.valor > 0 ? (r.valor / g.valor) * 100 : 0;
            return (
              <div key={r.uf}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <div style={{ color: T.text, fontWeight: 500 }}>
                    <b>{r.uf}</b> · {r.cidades.join(", ")} · {r.ativados} ativações
                  </div>
                  <div style={{ color: T.textSub }}>
                    {fmt.brl(r.valor)} <span style={{ color: T.textDim }}>({pct.toFixed(1)}%)</span>
                  </div>
                </div>
                <Progress value={r.valor} max={g.valor} color={T.brand} height={4} />
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
